// The horizontal rail entry point (APP-2183) — the inline teaser that wraps the
// immersive feed. It renders a horizontal scroll of video-poster cards from a
// real `/feed` source, ending in a "See them all" tile. Tapping a card opens the
// full-screen feed starting at that clip; "See them all" opens it from the top.
//
// Adopted from the Riffland Portals prototype (the `rail` variant of
// `PortalPhone.dc.html`). The rail owns only the entry point: it reports which
// clip to open via `onOpen`, leaving the caller (the demo page now, the embed in
// APP-2184 later) to mount the feed as a full-screen overlay.

// The shared wire contract both the feed proxy and this bundle speak, imported
// `import type` so esbuild erases it from the bundle.
import type { FeedItem, FeedPage } from "../shared/feed-contract.js";
import { injectStyles } from "./dom.js";
import { track, EVENTS } from "./track.js";

export interface MountRailOptions {
  /** The feed endpoint to read, e.g. `/feed/portal-christchurch`. */
  feedUrl: string;
  /**
   * Open the immersive feed. Called with a {@link FeedItem} id when a card is
   * tapped (open at that clip) and with no argument for "See them all" (open at
   * the top). The caller mounts {@link mountFeed}, passing `startId` through.
   */
  onOpen: (startId?: string) => void;
}

/** Handle returned by {@link mountRail}; `destroy` tears the rail down cleanly. */
export interface RailController {
  destroy: () => void;
}

// The rail is a peek, not the whole feed — it shows the first clips and defers
// the rest to "See them all". One upstream page is always more than enough.
const RAIL_CARD_LIMIT = 12;

const STYLE_ID = "crvf-rail-styles";

const ICON = {
  play: `<svg viewBox="0 0 24 24" fill="rgba(255,255,255,.95)"><path d="M7 4.5l13 7.5-13 7.5z"/></svg>`,
  up: `<svg viewBox="0 0 24 24" fill="none" stroke="#171412" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 19V5m-7 7 7-7 7 7"/></svg>`,
};

const CSS = `
.crvf-rail{font-family:system-ui,-apple-system,Segoe UI,Roboto,sans-serif}
.crvf-rail-scroller{display:flex;gap:12px;overflow-x:auto;overflow-y:hidden;padding:4px;scroll-snap-type:x mandatory;scrollbar-width:none;-ms-overflow-style:none}
.crvf-rail-scroller::-webkit-scrollbar{display:none}
.crvf-rail-card{position:relative;flex:none;width:132px;height:234px;border-radius:10px;overflow:hidden;background:#141310;cursor:pointer;scroll-snap-align:start;transition:transform .15s ease}
.crvf-rail-card:hover{transform:translateY(-3px)}
.crvf-rail-poster,.crvf-rail-video{position:absolute;inset:0;width:100%;height:100%;object-fit:cover}
.crvf-rail-scrim{position:absolute;inset:0;background:linear-gradient(180deg,rgba(0,0,0,.18) 0%,rgba(0,0,0,0) 40%,rgba(10,8,6,.78) 100%)}
.crvf-rail-glyph{position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);width:19px;height:19px;opacity:.82;filter:drop-shadow(0 1px 4px rgba(0,0,0,.5))}
.crvf-rail-label{position:absolute;left:10px;right:10px;bottom:10px;font-size:11px;font-weight:700;color:#fff;line-height:1.25;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.crvf-rail-more{position:relative;flex:none;width:132px;height:234px;border-radius:10px;overflow:hidden;background:#141310;cursor:pointer;scroll-snap-align:start}
.crvf-rail-more:hover{opacity:.92}
.crvf-rail-more-bg{position:absolute;inset:-14px;width:calc(100% + 28px);height:calc(100% + 28px);object-fit:cover;filter:blur(9px) saturate(1.1)}
.crvf-rail-more-scrim{position:absolute;inset:0;background:linear-gradient(180deg,rgba(10,8,6,.42),rgba(10,8,6,.62))}
.crvf-rail-more-inner{position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:11px;padding:0 14px;text-align:center}
.crvf-rail-more-badge{width:42px;height:42px;border-radius:999px;background:rgba(255,255,255,.92);display:flex;align-items:center;justify-content:center}
.crvf-rail-more-badge svg{width:17px;height:17px}
.crvf-rail-more-title{font-size:13px;font-weight:700;color:#fff;line-height:1.3;letter-spacing:-.1px}
.crvf-rail-message{padding:16px 4px;color:#9a938c;font-size:14px}
`;

class Rail implements RailController {
  private readonly root: HTMLDivElement;
  private readonly scroller: HTMLDivElement;
  private video: HTMLVideoElement | null = null;
  private railView: IntersectionObserver | null = null;
  private destroyed = false;

  constructor(
    private readonly container: HTMLElement,
    private readonly options: MountRailOptions,
  ) {
    injectStyles(container, STYLE_ID, CSS);
    container.textContent = "";

    this.root = this.div("crvf-rail");
    this.scroller = this.div("crvf-rail-scroller");
    this.root.appendChild(this.scroller);
    container.appendChild(this.root);

    this.observeRailView();
    void this.load();
  }

  // Fire `rail_view` once, the first time the rail scrolls into view — the top of
  // the funnel (reach). Rendered before the feed data arrives, so it measures the
  // teaser being seen, not the clips loading. Absent IntersectionObserver → skip.
  private observeRailView(): void {
    if (typeof IntersectionObserver === "undefined") return;
    this.railView = new IntersectionObserver((entries) => {
      if (!entries.some((e) => e.isIntersecting)) return;
      this.railView?.disconnect();
      this.railView = null;
      track(EVENTS.railView, { non_interaction: true });
    });
    this.railView.observe(this.root);
  }

  private div(className: string): HTMLDivElement {
    const el = document.createElement("div");
    el.className = className;
    return el;
  }

  private async load(): Promise<void> {
    try {
      const res = await fetch(this.options.feedUrl);
      if (!res.ok) throw new Error(`feed responded ${res.status}`);
      const page = (await res.json()) as FeedPage;
      if (this.destroyed) return;
      this.render(page.items.slice(0, RAIL_CARD_LIMIT));
    } catch {
      if (!this.destroyed) this.showMessage("This gallery couldn’t be loaded.");
    }
  }

  private render(items: FeedItem[]): void {
    const first = items[0];
    if (!first) {
      this.showMessage("No clips to show yet.");
      return;
    }
    items.forEach((item, i) => this.scroller.appendChild(this.card(item, i === 0)));
    this.scroller.appendChild(this.moreTile(first));
  }

  private card(item: FeedItem, live: boolean): HTMLDivElement {
    const el = this.div("crvf-rail-card");
    el.addEventListener("click", () => {
      track(EVENTS.feedOpen, { entry: "card", item_id: item.id });
      this.options.onOpen(item.id);
    });

    const poster = document.createElement("img");
    poster.className = "crvf-rail-poster";
    poster.src = item.poster.url;
    poster.alt = "";
    el.appendChild(poster);

    if (live) el.appendChild(this.liveVideo(item));

    el.appendChild(this.div("crvf-rail-scrim"));

    if (!live) {
      const glyph = this.div("crvf-rail-glyph");
      glyph.innerHTML = ICON.play;
      el.appendChild(glyph);
    }

    const creator = item.author?.screen_name;
    if (creator) {
      const label = this.div("crvf-rail-label");
      label.textContent = `@${creator}`;
      el.appendChild(label);
    }

    return el;
  }

  // The one autoplaying card: muted + looping, silent like everything else in the
  // rail (the audio gate lives inside the open feed). The muted attribute is
  // enforced defensively so autoplay is never blocked.
  private liveVideo(item: FeedItem): HTMLVideoElement {
    const v = document.createElement("video");
    v.className = "crvf-rail-video";
    v.loop = true;
    v.muted = true;
    v.defaultMuted = true;
    v.volume = 0;
    v.autoplay = true;
    v.playsInline = true;
    v.setAttribute("playsinline", "");
    v.preload = "auto";
    v.src = (item.playback.preview ?? item.playback.full).url;
    const keepMuted = (): void => {
      v.muted = true;
      v.volume = 0;
    };
    v.addEventListener("volumechange", keepMuted);
    v.addEventListener("loadeddata", () => void v.play().catch(() => {}));
    this.video = v;
    return v;
  }

  // The closing "See them all" tile: a blurred still behind an up-arrow badge.
  // Opens the feed from the top.
  private moreTile(first: FeedItem): HTMLDivElement {
    const el = this.div("crvf-rail-more");
    el.addEventListener("click", () => {
      track(EVENTS.feedOpen, { entry: "see_all" });
      this.options.onOpen();
    });

    const bg = document.createElement("img");
    bg.className = "crvf-rail-more-bg";
    bg.src = first.poster.url;
    bg.alt = "";
    el.appendChild(bg);

    el.appendChild(this.div("crvf-rail-more-scrim"));

    const inner = this.div("crvf-rail-more-inner");
    const badge = this.div("crvf-rail-more-badge");
    badge.innerHTML = ICON.up;
    inner.appendChild(badge);
    const title = this.div("crvf-rail-more-title");
    title.textContent = "See them all";
    inner.appendChild(title);
    el.appendChild(inner);

    return el;
  }

  private showMessage(text: string): void {
    const msg = this.div("crvf-rail-message");
    msg.textContent = text;
    this.scroller.replaceChildren(msg);
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    this.railView?.disconnect();
    this.railView = null;
    if (this.video) {
      this.video.pause();
      this.video.removeAttribute("src");
      this.video.load();
      this.video = null;
    }
    this.root.remove();
  }
}

/**
 * Mount the horizontal rail entry point into `container`, rendering poster cards
 * from `feedUrl` followed by a "See them all" tile. Tapping a card calls
 * `onOpen(item.id)`; "See them all" calls `onOpen()`. Returns a controller whose
 * `destroy()` tears everything down.
 */
export function mountRail(container: HTMLElement, options: MountRailOptions): RailController {
  return new Rail(container, options);
}
